import { QuantitySelectorComponent } from '@theme/component-quantity-selector';

/**
 * A custom element that allows the user to select a quantity in the cart.
 * Extends QuantitySelectorComponent but uses absolute max limits instead of effective max.
 * Semantics: "What should the total quantity BE in the cart" vs "How many to ADD to cart"
 *
 * @extends {QuantitySelectorComponent}
 */
class CartQuantitySelectorComponent extends QuantitySelectorComponent {
  /**
   * Gets the effective maximum value for cart quantity selector
   * Cart page: uses absolute max (how much can be in cart total)
   * @returns {number | null} The effective max, or null if no max
   */
  getEffectiveMax() {
    const { max } = this.getCurrentValues();
    return max;
  }

  /**
   * Updates button states based on current value and limits.
   * At quantity 1, minus button stays ENABLED so it can trigger removal.
   */
  updateButtonStates() {
    const { minusButton, plusButton } = this.refs;
    const { min, value } = this.getCurrentValues();
    const effectiveMax = this.getEffectiveMax();

    // Keep minus enabled at min so decreasing from 1 triggers remove
    minusButton.disabled = false;
    plusButton.disabled = effectiveMax !== null && value >= effectiveMax;
  }

  /**
   * Overrides decrease to trigger item removal when quantity is at minimum.
   * Fixes jank by disabling the button during the async update.
   * @param {Event} event
   */
  decreaseQuantity(event) {
    if (!(event.target instanceof HTMLElement)) return;
    event.preventDefault();

    const { value, min } = this.getCurrentValues();
    const { minusButton, plusButton } = this.refs;

    if (value <= min) {
      // Trigger remove — find and click the remove button in the same cart row
      const row = this.closest('tr, [data-key]');
      const removeButton = row?.querySelector('.cart-items__remove');
      if (removeButton) {
        removeButton.click();
      }
      return;
    }

    // Disable both buttons during async cart update to prevent double-clicks (fixes jank)
    minusButton.disabled = true;
    plusButton.disabled = true;

    this.updateQuantity(-1);

    // Re-enable after a short debounce to allow cart API to respond
    setTimeout(() => {
      this.updateButtonStates();
    }, 400);
  }

  /**
   * Overrides increase to disable buttons during async update (fixes jank).
   * @param {Event} event
   */
  increaseQuantity(event) {
    if (!(event.target instanceof HTMLElement)) return;
    event.preventDefault();

    const { minusButton, plusButton } = this.refs;

    // Disable during update to prevent rapid double-clicks
    minusButton.disabled = true;
    plusButton.disabled = true;

    this.updateQuantity(1);

    setTimeout(() => {
      this.updateButtonStates();
    }, 400);
  }
}

if (!customElements.get('cart-quantity-selector-component')) {
  customElements.define('cart-quantity-selector-component', CartQuantitySelectorComponent);
}